import React, { useCallback } from 'react';
import { useIntl } from 'react-intl';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { UpsellSurface } from 'fym-shared/src/music24/watermarkUpsell';
import { TypedMemo } from '../../../common/types';
import { useLicenseInfo } from '../../../../redux/slices/license/hooks/hooks';
import { NativeUpgradeNote } from '../../components/NativeUpgradeNote/NativeUpgradeNote';
import { SMLUpgradePlanOptions } from '../../components/SMLUpgradeModal/SMLUpgradePlanOptions';
import { SMLUpgradePromptActions } from '../../components/SMLUpgradeModal/SMLUpgradePromptActions';
import { SMLUpgradePromptHead } from '../../components/SMLUpgradeModal/SMLUpgradePromptHead';
import { upgradeFeatureIcons } from '../../components/SMLUpgradeModal/upgradeFeatureIcons';
import { featureMessages, upgradeMessages } from '../../components/SMLUpgradeModal/upgradeMessages';
import type { UpgradeOffer } from '../../components/SMLUpgradeModal/useUpgradeOffer';
import { SMLUpgradeFeature } from '../../config/music24PlanCapabilities';
import { planNameMessages } from '../../config/music24PlanNames';
import { useIsNativeEmbed } from '../../navigation/hooks/useIsNativeEmbed';
import { useIsUpsellMember } from '../useIsUpsellMember';
import { useUpsellEventFacts } from '../useUpsellEventFacts';
import { useUpsellShown } from '../useUpsellShown';
import { useWatermarkSummary } from '../useWatermarkSummary';
import { trackUpsellDismissed } from '../watermarkUpsellAnalytics';
import { useWatermarkPlanOptions } from './useWatermarkPlanOptions';
import { WatermarkGets } from './WatermarkGets';
import { WatermarkOfferMember } from './WatermarkOfferMember';
import { watermarkOfferMessages as messages } from './watermarkOfferMessages';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the upsell; each part uses its own slice of it */
import styles from '../WatermarkUpsell.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface WatermarkOfferProps {
  offer: UpgradeOffer;
  /** The surface that opened the offer. The checkout of `offer` carries it already. */
  surface: UpsellSurface;
  onClose: () => void;
  /** "Compare all plans": the plans window. */
  onCompare: () => void;
}

/**
 * The offer that each "Remove the watermark" button opens (design: trial-upsell b4). The owner
 * sees what a plan gives, Artist and Pro, and the checkout; a member sees the owner note; the
 * native web view sees no price and no checkout (the app sells the plan).
 */
const WatermarkOfferComponent: React.FC<WatermarkOfferProps> = ({ offer, surface, onClose, onCompare }) => {
  const intl = useIntl();
  const isMember = useIsUpsellMember();
  const isNativeEmbed = useIsNativeEmbed();
  const licenseInfo = useLicenseInfo();
  const { summary } = useWatermarkSummary();
  const eventFacts = useUpsellEventFacts();
  const options = useWatermarkPlanOptions({
    plans: offer.plans,
    selectedPlanId: offer.plan.id,
    hidePrice: isNativeEmbed,
  });
  useUpsellShown('offer', true);

  const dismiss = useCallback(() => {
    trackUpsellDismissed(eventFacts(surface));
    onClose();
  }, [eventFacts, onClose, surface]);

  if (isMember) {
    return (
      <WatermarkOfferMember
        files={summary.files}
        cleanCopiesAvailable={summary.cleanCopiesAvailable}
        onClose={dismiss}
      />
    );
  }

  const planName = intl.formatMessage(planNameMessages[offer.plan.tier]);
  const trialEndsAt = licenseInfo.isTrialActive ? licenseInfo.trialEndsAt : null;

  return (
    <div className={styles.modal} data-testid="watermark-offer">
      <SMLUpgradePromptHead
        icon={<FontAwesomeIcon icon={upgradeFeatureIcons[SMLUpgradeFeature.watermark]} />}
        title={intl.formatMessage(featureMessages.watermarkTitle)}
        body={intl.formatMessage(messages.lede)}
        onClose={dismiss}
      />
      <WatermarkGets summary={summary} variant="offer" />
      <SMLUpgradePlanOptions options={options} onSelect={offer.selectPlan} />
      {summary.needsPro > 0 && (
        <p className={styles.fine} data-testid="watermark-offer-needs-pro">
          {intl.formatMessage(messages.needsPro, { plan: intl.formatMessage(planNameMessages.pro) })}
        </p>
      )}
      {isNativeEmbed && <NativeUpgradeNote />}
      <SMLUpgradePromptActions
        primaryLabel={
          isNativeEmbed
            ? undefined
            : intl.formatMessage(offer.isCheckoutPending ? upgradeMessages.upgradeCtaBusy : upgradeMessages.upgradeTo, {
                plan: planName,
              })
        }
        onPrimary={offer.startCheckout}
        primaryDisabled={offer.isCheckoutPending}
        secondaryLabel={intl.formatMessage(upgradeMessages.compareAllPlans)}
        onSecondary={onCompare}
        laterLabel={intl.formatMessage(upgradeMessages.notNow)}
        onLater={dismiss}
      />
      {!isNativeEmbed && (
        <p className={styles.fine}>
          {trialEndsAt === null
            ? intl.formatMessage(messages.fine)
            : intl.formatMessage(messages.fineTrial, {
                date: intl.formatDate(trialEndsAt, { day: 'numeric', month: 'short' }),
              })}
        </p>
      )}
    </div>
  );
};

export const WatermarkOffer = TypedMemo(WatermarkOfferComponent);
